import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useUrlSearchParams } from "use-url-search-params";
import cx from "classnames";

import { useOrganizationList } from "modules/serviceRegister/hooks";
import { MANDATORY_CATEGORY_TYPE } from "modules/mandatoryRequirement/constants";
import { useList } from "modules/serviceRegister/hooks";

import { Table, Flex } from "antd";
import * as Grid from "components/Grid";

import StatCard from "components/Card";
import Spacer from "components/Spacer";
import Button from "components/Button";
import Container from "components/Container";

import cls from "./Tabs.module.scss";

import FilterList from "pages/ServiceRegisterList/components/FilterList";
import ExcelDownload from "pages/ServiceRegisterList/components/ExcelDownload";
import Pagination from "containers/Pagination";

import { LoadingOutlined } from "@ant-design/icons";
import * as Icons from "assets/icons";

const Tabs = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [type, setType] = useState(MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS);

  const [params, setParams] = useUrlSearchParams();
  const [search, setSearch] = useState(0);

  const { items: statItems, isFetched: isStatsFetched } = useOrganizationList({
    enabled: true,
    params: {
      perPage: 8,
    }
  });

  const defaultParams = {
    search,
    page: params.page,
    perPage: params.perPage,
    filter: [
      {
        key: "organization_id",
        operation: "=",
        value: params.organization_id,
        type: "STRING",
      },
      {
        key: "function_name.sphere.id",
        operation: "=",
        value: params.sphere_id,
        type: "NUMBER",
      },
      {
        key: `service_name`,
        operation: "%_%",
        value: params.service_name,
        type: "JSON",
      },
      {
        key: "service_form",
        operation: "=",
        value: params.service_form,
        type: "STRING",
      },
      {
        key: "subject_type",
        operation: "=",
        value: params.subject_type,
        type: "STRING",
      },
      {
        key: "payment_type",
        operation: "=",
        value: params.payment_type,
        type: "STRING",
      },
      {
        key: "service_area_id",
        operation: "=",
        value: params.service_area,
        type: "NUMBER"
      }
    ],
  }
  const { items, meta, isFetched } = useList({
    params: defaultParams
  });

  return (
    <section className={cls.section}>
      <Container>
        <div className={cls.tabs}>
          <div
            className={cx(cls.tab, type === MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS && cls.tabActive)}
            onClick={() => setType(MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS)}
            key={MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS}
          >
            {t(`mandatory_${MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS}`)}
          </div>
          <div
            className={cx(cls.tab, type === 'SERVICE_REGISTER' && cls.tabActive)}
            onClick={() => setType('SERVICE_REGISTER')}
            key={'SERVICE_REGISTER'}
          >
            {t('SERVICE_REGISTER')}
          </div>
        </div>

        <Spacer size={50} />

        <Grid.Row gutter={[16, 16]}>
          {type === MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS && statItems
            .map((item, index) => (
              <Grid.Col span={6} key={item.id} data-aos="fade-up" data-aos-delay={index * 100}>
                <StatCard
                  id={item.id}
                  name={item.name[i18n.language]}
                  uuid={item.file_response.uuid}
                  count={item.count}
                  onClick={() => navigate(`/service-register/list?organization_id=${item.id}`)}
                />
              </Grid.Col>
            ))}

          {type === MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS && !isStatsFetched && (
            <div className={cls.loading}>
              <LoadingOutlined />
            </div>
          )}
        </Grid.Row>

        {statItems.length > 0 && type === MANDATORY_CATEGORY_TYPE.ORGANIZATION_STATS && (
          <div className={cls.btn}>
            <Button onClick={() => navigate('/service-organizations')}>
              {t('action_more')}
            </Button>
          </div>
        )}

        <Grid.Row gutter={[16, 16]}>
          {type === 'SERVICE_REGISTER' && (<Grid.Col span={24}>
            <Flex gap={20} align="flex-end">
              <FilterList {...{ params, setParams, setSearch }} />
              <ExcelDownload defaultParams={defaultParams} />
            </Flex>

            <Spacer size={20} />
            <Table
              rowKey="id"
              defaultSort="-id"
              columns={[
                {
                  title: "№",
                  render: (text, record, index) =>
                    (meta.current - 1) * meta.perPage + index + 1,
                },
                {
                  key: "uniqueNumber",
                  title: t("column_unique_number"),
                  dataIndex: ["uniqueNumber"],
                },
                {
                  key: "service_name",
                  title: t("column_service_name"),
                  dataIndex: ["serviceName", i18n.language],
                },
                {
                  key: "users_type",
                  title: t("column_users_type"),
                  dataIndex: ["subjectType"],
                  render: value => t(value)
                },
                {
                  key: "service_form",
                  title: t("column_service_form"),
                  dataIndex: ["serviceForm"],
                  render: value => t(value)
                },

                {
                  key: "paymentType",
                  title: t("column_with_payment"),
                  dataIndex: ["paymentType"],
                  render: value => t(value)
                },
                {
                  key: "service_response",
                  title: t("column_service_response"),
                  dataIndex: ["serviceResult"],
                },
                {
                  key: "actions",
                  render: (_, record) => (
                    <Button
                      background="secondary"
                      color="secondary"
                      border="none"
                      radius="xs"
                      iconOnly
                      onClick={() => {
                        navigate(`/service-register/view/${record.id}`);
                      }}>
                      <Icons.ArrowRight />
                    </Button>
                  ),
                },
              ]}
              dataSource={items}
              pagination={false}
              loading={!isFetched}
              onRow={record => {
                return {
                  onClick: event => {
                    if (event.target.localName === "td")
                      navigate(`/service-register/view/${record.id}`);
                  },
                };
              }}
              scroll={{ x: 1400 }}
            />

            <Spacer size={32} />

            <Pagination
              current={meta.current}
              perPage={meta.perPage}
              total={meta.totalPages}
              currentItems={items.length}
              totalItems={meta.totalItems}
              onChange={(page) => setParams({ page })}
              onPerPageChange={perPage => setParams({ perPage })}
            />
          </Grid.Col>
          )}
        </Grid.Row>
      </Container>
    </section>
  )
}

export default Tabs;